import { useCallback, useEffect, useRef, useState } from "react";

const APPLY_MARKER = "cliqsuite:pwa-update-applied-at";
const APPLY_GUARD_MS = 30_000;
const DEFAULT_CHECK_INTERVAL_MS = 5 * 60_000;
const IDLE_CHECK_INTERVAL_MS = 5_000;
const ACTIVATION_RELOAD_FALLBACK_MS = 4_000;
const CHANNEL_NAME = "cliqsuite:pwa-update";

const CSS = `
.pwa-update { position: fixed; left: 50%; bottom: calc(22px + env(safe-area-inset-bottom)); transform: translateX(-50%); z-index: 500; display: flex; align-items: center; gap: 12px; min-height: 54px; max-width: calc(100vw - 24px); padding: 7px 8px 7px 18px; border-radius: 27px; background: #20242E; color: #fff; font-size: 14px; font-weight: 700; box-shadow: 0 14px 30px rgba(32, 36, 46, 0.3); }
.pwa-update > .ms { font-size: 20px; flex: 0 0 auto; }
.pwa-update__msg { flex: 1; min-width: 0; }
.pwa-update button { border: none; font-family: inherit; flex: 0 0 auto; white-space: nowrap; cursor: pointer; }
.pwa-update__later { background: transparent; color: rgba(255, 255, 255, 0.7); font-size: 13px; font-weight: 800; padding: 0 6px; }
.pwa-update__go { height: 40px; padding: 0 16px; border-radius: 10px; background: #F8F9FA; color: #20242E; font-size: 13.5px; font-weight: 800; box-shadow: inset 0 0 0 1.5px #DDDFE6; }
.pwa-update__go:disabled { cursor: wait; opacity: 0.7; }
@media (max-width: 767px) {
  .pwa-update { left: 12px; right: 12px; bottom: auto; top: calc(92px + env(safe-area-inset-top)); transform: none; max-width: none; min-height: 0; gap: 10px; padding: 10px 10px 10px 14px; border-radius: 18px; font-size: 13px; line-height: 1.35; }
  .pwa-update__go { height: 36px; padding: 0 12px; font-size: 12.5px; }
}
`;

export interface PwaUpdateBannerProps {
  needRefresh: boolean;
  setNeedRefresh: (value: boolean) => void;
  updateServiceWorker: () => Promise<void>;
  registration: ServiceWorkerRegistration | null;
  message: string;
  /** Automatically apply once this tab is safe to reload. */
  isIdle?: () => boolean;
  checkIntervalMs?: number;
  allowDismiss?: boolean;
}

function recentlyApplied(): boolean {
  try {
    return Date.now() - Number(sessionStorage.getItem(APPLY_MARKER) ?? 0) < APPLY_GUARD_MS;
  } catch {
    return false;
  }
}

function markApplied(): void {
  try {
    sessionStorage.setItem(APPLY_MARKER, String(Date.now()));
  } catch {
    // A blocked sessionStorage must not prevent an update.
  }
}

/**
 * Shared service-worker lifecycle for every CliqSuite PWA. Normal updates
 * activate Workbox's fully-prepared waiting worker; they never unregister a
 * worker or delete caches, so the current release remains available until
 * the replacement is complete. IndexedDB/localStorage are never touched.
 */
export function PwaUpdateBanner({
  needRefresh,
  setNeedRefresh,
  updateServiceWorker,
  registration,
  message,
  isIdle,
  checkIntervalMs = DEFAULT_CHECK_INTERVAL_MS,
  allowDismiss = true,
}: PwaUpdateBannerProps) {
  const [applying, setApplying] = useState(false);
  const [applyFailed, setApplyFailed] = useState(false);
  const applyingRef = useRef(false);

  // Long-running installed PWAs do not naturally navigate, and mobile OSes
  // freeze timers while backgrounded. Check on a timer and on every common
  // resume signal, with complete cleanup when the component unmounts.
  useEffect(() => {
    if (!registration) return;
    const check = () => {
      if (navigator.onLine) void registration.update().catch(() => undefined);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") check();
    };
    check();
    const interval = window.setInterval(check, checkIntervalMs);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", check);
    window.addEventListener("focus", check);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", check);
      window.removeEventListener("focus", check);
    };
  }, [checkIntervalMs, registration]);

  // Tell sibling tabs an update is being activated. controllerchange is the
  // authoritative reload signal: it fires only after the new worker owns the
  // page, preventing a reload back into the old release.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    let shouldReload = false;
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (event: MessageEvent<{ type?: string }>) => {
        if (event.data?.type === "applying") shouldReload = true;
      };
    } catch {
      // BroadcastChannel is optional; controllerchange still reaches tabs.
    }
    const onControllerChange = () => {
      if (!shouldReload && !applyingRef.current) return;
      markApplied();
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      channel?.close();
    };
  }, []);

  const apply = useCallback(async () => {
    if (applyingRef.current) return;
    applyingRef.current = true;
    setApplying(true);
    setApplyFailed(false);
    markApplied();
    try {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channel.postMessage({ type: "applying" });
      channel.close();
    } catch {
      // Optional cross-tab coordination.
    }
    try {
      await updateServiceWorker();
      // Workbox normally reloads through `controllerchange`, but some
      // installed/mobile browsers resolve the update request without
      // delivering that event to the currently frozen/resumed page. A
      // bounded fallback navigation makes the newly activated release run
      // without asking operators to hard-refresh or unpair. This is a plain
      // reload: pairing, IndexedDB/PGlite and all other device data remain.
      window.setTimeout(() => {
        if (applyingRef.current) window.location.reload();
      }, ACTIVATION_RELOAD_FALLBACK_MS);
    } catch {
      applyingRef.current = false;
      setApplying(false);
      setApplyFailed(true);
    }
  }, [updateServiceWorker]);

  // Operational screens can supply an idle predicate. A loop guard prevents
  // a broken deployment from continuously reloading; the visible button is
  // retained as a manual recovery path.
  useEffect(() => {
    if (!needRefresh || !isIdle || applyingRef.current || recentlyApplied()) return;
    const tryApply = () => {
      if (isIdle()) void apply();
    };
    tryApply();
    if (applyingRef.current) return;
    const interval = window.setInterval(tryApply, IDLE_CHECK_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [apply, isIdle, needRefresh]);

  if (!needRefresh) return null;
  // Canvas: a dark pill at the bottom on a desktop or tablet (v2-d-sell-update); on a phone a card
  // under the head, full width (v2-g-menu-update). Both stay out of the way of the work.
  return (
    <div role="status" aria-live="polite" className="pwa-update">
      <style>{CSS}</style>
      <span className="ms" aria-hidden="true">
        system_update
      </span>
      <span className="pwa-update__msg">{applyFailed ? "The update couldn't start. Check the connection and try again." : message}</span>
      {allowDismiss && !applying && (
        <button type="button" className="pwa-update__later" onClick={() => setNeedRefresh(false)}>
          Later
        </button>
      )}
      <button type="button" className="pwa-update__go" disabled={applying} onClick={() => void apply()}>
        {applying ? "Updating…" : applyFailed ? "Try again" : "Update now"}
      </button>
    </div>
  );
}
